import React, { useState, useEffect, forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './AppointmentForm.css';


const usStates = [ 'New York', 'New Jersey', 'Georgia', 'Washington', 'Massachusetts', 'California', 'Florida', 'Texas', 'Illinois' ];


const formatTime = (time) => {
    if (!time) return '';
    const [hour, minute] = time.split(':');
    const hourInt = parseInt(hour, 10);
    const ampm = hourInt >= 12 ? 'PM' : 'AM';
    const formattedHour = hourInt % 12 || 12; 
    return `${formattedHour}:${minute} ${ampm}`;
};

const AppointmentForm = () => {
 
    const [formData, setFormData] = useState({
        state: '', name: '', surname: '', email: '', phone: '', address: '',
        streetAddress: '', streetAddress2: '', city: '', zipCode: ''
    });
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);
    const [fileName, setFileName] = useState('');
    const [chatParams, setChatParams] = useState({ chat_id: null, type: null, account_id: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [messages, setMessages] = useState([]); 

    useEffect(() => {
        localStorage.clear();
        setMessages([]); // Mesajları sıfırla
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const chatId = queryParams.get('chat_id');
        const appointmentType = queryParams.get('type');
        const accountId = queryParams.get('account_id');
        if (!chatId || !appointmentType || !accountId) {
            setError("Invalid form address. Please try again from the chat.");
        } else {
            setChatParams({ chat_id: chatId, type: appointmentType, account_id: accountId });
        }
    }, []);

    
    useEffect(() => {
        if (!selectedDate || !chatParams.chat_id) return;
        const fetchSlots = async () => {
            setSlotsLoading(true);
            setAvailableSlots([]);
            setSelectedTime(null);
            const dateStr = selectedDate.toISOString().split('T')[0];
            const API_URL = `${process.env.REACT_APP_API_URL}/available-slots?date=${dateStr}`;
            try {
                const response = await fetch(API_URL);
                if (!response.ok) throw new Error('No available slots found.');
                const data = await response.json();
                setAvailableSlots(data.available_slots || []);
            } catch (err) {
                setAvailableSlots([]);
            } finally {
                setSlotsLoading(false);
            }
        };
        fetchSlots();
    }, [selectedDate, chatParams.chat_id]);

  
    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };
    
   
    const handleFileChange = (e) => {
        setFileName(e.target.files.length > 0 ? e.target.files[0].name : '');
    };
    
  
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedTime) {
            setError('Please select an appointment time.');
            return;
        }
        setError('');
        setLoading(true);
        
        const detailedAddress = `${formData.streetAddress}${formData.streetAddress2 ? `, ${formData.streetAddress2}` : ''}, ${formData.city}, ${formData.state} ${formData.zipCode}`;
        const finalNotes = fileName ? `Uploaded file: ${fileName}` : '';
        const combinedAddress = formData.address ? `${formData.address}\nDetailed Address: ${detailedAddress}` : detailedAddress;

        const payload = {
            ...chatParams,
            name: formData.name, surname: formData.surname, email: formData.email, phone: formData.phone,
            address: combinedAddress,
            date: selectedDate.toISOString().split('T')[0], time: selectedTime,
            notes: finalNotes
        };

        try {
            const API_URL = `${process.env.REACT_APP_API_URL}/appointment-callback`;
            const response = await fetch(API_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('An error occurred while creating the appointment.');
            setSuccess('Your appointment has been successfully created! You can return to the chat window.');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    
  
    const CustomDateInput = forwardRef(({ value, onClick }, ref) => (
        <div className="custom-date-input" onClick={onClick} ref={ref}>
            {value}
            <span className="calendar-icon"></span>
        </div>
    ));

   
    if (!chatParams.chat_id) return <div className="form-wrapper"><div className="form-container error-message">{error}</div></div>;
    if (success) return <div className="form-wrapper"><div className="form-container success-message">{success}</div></div>;

    return (
        <div className="form-wrapper">
            <div className="form-container">
                <div className="form-header">
                    <span className="back-arrow">←</span>
                </div>
                <form onSubmit={handleSubmit}>
                    {}
                    <div className="form-group">
                        <label>Please select your state *</label>
                        <select name="state" value={formData.state} onChange={handleChange} required>
                            <option value="" disabled>Please Select</option>
                            {usStates.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="form-row">
                        <div className="form-group"><label>Name *</label><input type="text" name="name" onChange={handleChange} required /></div>
                        <div className="form-group"><label>Surname *</label><input type="text" name="surname" onChange={handleChange} required /></div>
                    </div>
                    <div className="form-row">
                        <div className="form-group"><label>Email *</label><input type="email" name="email" onChange={handleChange} required /></div>
                        <div className="form-group"><label>Phone *</label><input type="tel" name="phone" placeholder="(000) 000-0000" onChange={handleChange} required /></div>
                    </div>
                    <div className="form-group">
                        <label>Address *</label>
                        <textarea name="address" rows="4" onChange={handleChange} placeholder="Please fill out this field." required></textarea>
                    </div>

                    {}
                    <div className="date-time-section">
                        <div className="datepicker-wrapper">
                             <label>Select a Date *</label>
                            <DatePicker
                                selected={selectedDate}
                                onChange={date => setSelectedDate(date)}
                                minDate={new Date()}
                                customInput={<CustomDateInput />}
                                dateFormat="MM/dd/yyyy"
                                calendarClassName="custom-calendar"
                                renderCustomHeader={({
                                    date,
                                    changeYear,
                                    changeMonth,
                                }) => (
                                    <div className="datepicker-custom-header">
                                        <select
                                            value={date.getFullYear()}
                                            onChange={({ target: { value } }) => changeYear(value)}
                                        >
                                            {[2024, 2025, 2026, 2027].map(year => <option key={year} value={year}>{year}</option>)}
                                        </select>
                                        <select
                                            value={date.toLocaleString('en-US', { month: 'long' })}
                                            onChange={({ target: { value } }) => changeMonth(new Date(Date.parse(value +" 1, 2012")).getMonth())}
                                        >
                                            {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map(month => <option key={month} value={month}>{month}</option>)}
                                        </select>
                                    </div>
                                )}
                            />
                        </div>
                        <div className="time-slots-wrapper">
                             <div className="time-header">
                                <p>{selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                                <div className="time-nav">
                                    <button type="button">‹</button>
                                    <button type="button">›</button>
                                </div>
                             </div>
                            <div className="time-slots-container">
                                {slotsLoading ? <p className="slots-message">Loading times...</p> :
                                availableSlots.length > 0 ? (
                                    availableSlots.map(slot => (
                                        <button type="button" key={slot} className={`time-slot-btn ${selectedTime === slot ? 'selected' : ''}`} onClick={() => setSelectedTime(slot)}>
                                            {formatTime(slot)}
                                        </button>
                                    ))
                                ) : <p className="slots-message">No available slots found.</p>
                                }
                            </div>
                        </div>
                    </div>
                    <div className="form-group timezone-selector">
                       
                    </div>

                    {}
                    <div className="form-group">
                        <label className="file-section-label">If available, please attach your home's floor plan; if not, you may upload photos instead.</label>
                        <div className="file-drop-area">
                            <input type="file" onChange={handleFileChange} className="file-input" id="file"/>
                            <label htmlFor="file" className="file-label">
                                <span className="upload-icon"></span>
                                <p>Browse Files</p>
                                <small>Drag and drop files</small>
                            </label>
                        </div>
                    </div>
                    <div className="address-details-section">
                        <h2 className="section-title">Address</h2>
                        <div className="form-group"><label>Street Address</label><input type="text" name="streetAddress" onChange={handleChange} /></div>
                        <div className="form-group"><label>Street Address Line 2</label><input type="text" name="streetAddress2" onChange={handleChange} /></div>
                        <div className="form-row">
                            <div className="form-group"><label>City</label><input type="text" name="city" onChange={handleChange} /></div>
                            <div className="form-group"><label>State / Province</label><input type="text" name="state" value={formData.state} onChange={handleChange} readOnly/></div>
                        </div>
                         <div className="form-group zip-code-group"><label>Postal / Zip Code</label><input type="text" name="zipCode" onChange={handleChange} /></div>
                    </div>
                    
                    {}
                    {error && <div className="error-message">{error}</div>}
                    <div className="submit-container">
                        <button type="submit" className="submit-btn" disabled={loading}>{loading ? 'SUBMITTING...' : 'SUBMIT'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AppointmentForm;